<template>
  <quick-number-scope
    v-if="isScopePropertyType(propertyType.number)"
    :open="open"
    :scope-item="scopeItem"
    :node="node"
    :read-only="readOnly"
    :hide-is-empty="hideIsEmpty"
    @update:open="emit('update:open', $event)"
    @update:scope="updateScope"
    @update:not-operator="updateNotOperator"
    @delete="onDelete"
    @add-to-advanced="onAddToAdvanced"
  >
    <template #trigger>
      <slot />
    </template>
  </quick-number-scope>
  <quick-date-scope
    v-if="isScopePropertyType(propertyType.date)"
    :open="open"
    :scope-item="scopeItem"
    :node="node"
    :read-only="readOnly"
    :has-dynamic-values="hasDynamicDates"
    :hide-is-empty="hideIsEmpty"
    @update:open="emit('update:open', $event)"
    @update:scope="updateScope"
    @delete="onDelete"
    @add-to-advanced="onAddToAdvanced"
  >
    <template #trigger>
      <slot />
    </template>
  </quick-date-scope>
  <quick-user-scope
    v-else-if="isScopePropertyType(propertyType.user)"
    :open="open"
    :scope-item="scopeItem"
    :node="node"
    :read-only="readOnly"
    :hide-is-empty="hideIsEmpty"
    :max-tag-text-length="maxTagTextLength"
    :field-name="userScopeFieldName"
    @update:open="emit('update:open', $event)"
    @update:scope="updateScope"
    @update:not-operator="updateNotOperator"
    @delete="onDelete"
    @add-to-advanced="onAddToAdvanced"
  >
    <template #trigger>
      <slot />
    </template>
  </quick-user-scope>
  <quick-space-scope
    v-else-if="isScopePropertyType(propertyType.space)"
    :open="open"
    :scope-item="scopeItem"
    :node="node"
    :read-only="readOnly"
    :hide-is-empty="hideIsEmpty"
    :max-tag-text-length="maxTagTextLength"
    @update:open="emit('update:open', $event)"
    @update:scope="updateScope"
    @update:not-operator="updateNotOperator"
    @delete="onDelete"
    @add-to-advanced="onAddToAdvanced"
  >
    <template #trigger>
      <slot />
    </template>
  </quick-space-scope>
  <quick-option-scope
    v-else-if="isScopePropertyType(propertyType.options) || isScopePropertyType(propertyType.singleSelect)"
    :open="open"
    :scope-item="scopeItem"
    :node="node"
    :options="property.options"
    :read-only="readOnly"
    :hide-is-empty="hideIsEmpty"
    :max-tag-text-length="maxTagTextLength"
    @update:open="emit('update:open', $event)"
    @update:scope="updateScope"
    @update:not-operator="updateNotOperator"
    @delete="onDelete"
    @add-to-advanced="onAddToAdvanced"
  >
    <template #trigger>
      <slot />
    </template>
  </quick-option-scope>
  <quick-status-scope
    v-else-if="isScopePropertyType(propertyType.status)"
    :open="open"
    :scope-item="scopeItem"
    :node="node"
    :read-only="readOnly"
    :max-tag-text-length="maxTagTextLength"
    @update:open="emit('update:open', $event)"
    @update:scope="updateScope"
    @update:not-operator="updateNotOperator"
    @delete="onDelete"
    @add-to-advanced="onAddToAdvanced"
  >
    <template #trigger>
      <slot />
    </template>
  </quick-status-scope>
  <quick-lookup-scope
    v-else-if="isScopePropertyType(propertyType.lookup)"
    :open="open"
    :scope-item="scopeItem"
    :node="node"
    :read-only="readOnly"
    :max-tag-text-length="maxTagTextLength"
    @update:open="emit('update:open', $event)"
    @update:scope="updateScope"
    @update:not-operator="updateNotOperator"
    @delete="onDelete"
    @add-to-advanced="onAddToAdvanced"
  >
    <template #trigger>
      <slot />
    </template>
  </quick-lookup-scope>
  <quick-goal-scope
    v-else-if="isScopePropertyType(GOAL_RELATION)"
    :open="open"
    :scope-item="scopeItem"
    :node="node"
    :field-name="userScopeConfig.edges.relation"
    :read-only="readOnly"
    :hide-is-empty="hideIsEmpty"
    @update:open="emit('update:open', $event)"
    @update:scope="updateScope"
    @update:not-operator="updateNotOperator"
    @delete="onDelete"
    @add-to-advanced="onAddToAdvanced"
  >
    <template #trigger>
      <slot />
    </template>
  </quick-goal-scope>
</template>

<script setup>
import QuickDateScope from '@/components/filter/quick/QuickDateScope.vue';
import QuickGoalScope from '@/components/filter/quick/QuickGoalScope.vue';
import QuickLookupScope from '@/components/filter/quick/QuickLookupScope.vue';
import QuickNumberScope from '@/components/filter/quick/QuickNumberScope.vue';
import QuickOptionScope from '@/components/filter/quick/QuickOptionScope.vue';
import QuickSpaceScope from '@/components/filter/quick/QuickSpaceScope.vue';
import QuickStatusScope from '@/components/filter/quick/QuickStatusScope.vue';
import QuickUserScope from '@/components/filter/quick/QuickUserScope.vue';
import useDebounce from '@/composables/debounce';
import useProperties from '@/composables/property/property';
import useScopeItem from '@/composables/filter/scope-item';
import { GOAL_RELATION } from '@/lib/props/custom-types';
import { computed, ref, toRef, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { propertyType, userScopeType } from 'shared/constants.json';
import { userScope as userScopeConfig } from 'shared/api/query/configs.json';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  filter: {
    type: Object,
    required: true,
  },
  scopeItems: {
    type: Array,
    default: () => [],
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  maxTagTextLength: {
    type: Number,
    default: 0,
  },
  hasDynamicDates: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:open', 'update:filter', 'delete', 'addToAdvanced']);

const open = toRef(props, 'open');
const filter = ref(copy(props.filter));
watch(() => props.filter, (val) => {
  if (JSON.stringify(filter.value) === JSON.stringify(val)) {
    return;
  }
  filter.value = copy(val);
});
const { debounce } = useDebounce();
watch(filter, (val) => {
  debounce(() => emit('update:filter', val), 5);
}, { deep: true });

const onDelete = () => {
  emit('update:open', false);
  emit('delete');
};
const onAddToAdvanced = () => {
  emit('update:open', false);
  emit('addToAdvanced');
};

const scopeItemSvc = useScopeItem();
const node = computed(() => filter.value.children[0]);
const op = computed(() => node.value.op);
const scope = computed(() => node.value.children[0].scope);
const scopeItem = computed(() => scopeItemSvc.selectedScopeItem(props.scopeItems, scope.value));
const scopeType = computed(() => scopeItemSvc.selectedScopeType(scope.value));
const scopePropertyType = computed(() => scopeItemSvc.selectedScopePropertyType(scopeItem.value));
const hideIsEmpty = computed(() => {
  switch (scopeType.value) {
    case userScopeType.staticUsers:
      return true;
    case userScopeType.directProperty:
      return scopeItem.value.directProperty.noIsEmptyFilter;
    case userScopeType.property:
    default:
      return false;
  }
});

const isScopePropertyType = (type) => type === scopePropertyType.value;

const updateScope = (scopeParam) => {
  if (JSON.stringify(scope.value) === JSON.stringify(scopeParam)) {
    return;
  }
  filter.value.children[0].children[0] = { ...filter.value.children[0].children[0], scope: scopeParam };
};
const updateNotOperator = (opParam) => {
  if (op.value === opParam) {
    return;
  }
  filter.value.children[0] = { ...filter.value.children[0], op: opParam };
};

// Specific components needs
const propertySvc = useProperties();
const property = computed(() => propertySvc.selectSingle(scopeItem.value.property.uid));

const userScopeFieldName = computed(() => {
  switch (scopeType.value) {
    case userScopeType.staticUsers:
      return userScopeConfig.edges.staticUsers;
    case userScopeType.directProperty:
    case userScopeType.property:
    default:
      return userScopeConfig.edges.users;
  }
});
</script>

<style scoped lang="scss">
.quick-filter {
  display: flex;
  flex-direction: column;

  ._body {
    max-width: 250px;
  }
}
</style>
